import { Link } from 'react-router-dom';
import { SignupForm } from 'features/auth/components/signup-form';
import darklogo from 'assets/images/bebrave-group-dark.svg';
import lightlogo from 'assets/images/bebrave-group.svg';
import { useTheme } from 'components/core/theme-provider';
import { useGetLoginOptions } from 'features/auth/hooks/use-auth';
import { GRANT_TYPES } from 'constant/auth';
import { SsoSignin } from '../signin/signin-sso';
import { useTranslation } from 'react-i18next';
import { useWhiteLabelStore } from 'state/store/white-label';

export function SignupPage() {
  const { theme } = useTheme();
  const { data: loginOption } = useGetLoginOptions();
  const { settings: whiteLabelSettings } = useWhiteLabelStore();

  // Determine which logo to use based on theme and white-label settings
  const logoSrc = theme === 'dark'
    ? (whiteLabelSettings?.logoUrlDarkMode || darklogo)
    : (whiteLabelSettings?.logoUrl || lightlogo);

  const socialGrantAllowed = loginOption?.allowedGrantTypes?.includes(GRANT_TYPES.social);

  return (
    <div className="flex flex-col gap-6">
      <Logo logoSrc={logoSrc} />
      <SignupHeader />
      <SignupForm />
      {socialGrantAllowed && loginOption && <SsoSignin loginOption={loginOption} />}
    </div>
  );
}

type LogoProps = Readonly<{
  logoSrc: string;
}>;

function Logo({ logoSrc }: LogoProps) {
  return (
    <div className="w-40">
      <img src={logoSrc} onError={(e) => {
        const target = e.target as HTMLImageElement;
        if (target.src !== logoSrc) {
          target.src = lightlogo;
        }
      }} className="h-10 object-contain" alt="logo" />
    </div>
  );
}

function SignupHeader() {
  const { t } = useTranslation();

  return (
    <div>
      <h2 className="text-2xl font-bold text-high-emphasis">{t('SIGN_UP_ACCESS_OPEN_SOURCE')}</h2>
      <div className="flex items-center gap-1 mt-1">
        <span className="text-sm font-normal text-medium-emphasis">
          {t('ALREADY_HAVE_ACCOUNT')}
        </span>
        <Link
          to={'/login'}
          className="text-sm font-bold text-primary hover:text-primary-600 hover:underline"
        >
          {t('LOG_IN')}
        </Link>
      </div>
    </div>
  );
}

type DividerProps = Readonly<{
  text: string;
}>;

export function Divider({ text }: DividerProps) {
  return (
    <div className="flex items-center gap-4 mb-4">
      <div className="flex-1">
        <hr className="h-[2px] bg-gray-200 border-0 rounded" />
      </div>
      <div className="text-base font-normal text-low-emphasis">{text}</div>
      <div className="flex-1">
        <hr className="h-[2px] bg-gray-200 border-0 rounded" />
      </div>
    </div>
  );
}
